import { usePageModels } from '@/context/PageModels';
import Button from '@/features/shared/Button';
import Chip from '@/features/shared/Chip';
import {
  datalayerPush,
  getDataLayerNumFilters,
  getDataLayerSearchFilters,
  getDataLayerSearchFiltersObject,
} from '@/features/shared/HOCs/withGTM';
import { useIsRevision } from '@/features/shared/Hooks';
import { useGetLanguageLabels } from '@/features/shared/Hooks/facets/useProcessFacets';
import { ColorNames, LoadingStatusValues, Theme } from '@/utils/constants';
import { reduceChipDefault, reducePublicationDateChip } from '@/utils/reducers/reduceFilter';
import { Close as CloseIcon } from '@material-ui/icons';
import { isEmpty } from 'lodash/fp';
import React, { memo, useContext, useRef } from 'react';
import TagManager from 'react-gtm-module';
import ElasticComponentContext from '../ElasticComponentContext';
import useStyles from './ChipFilters.styles';

interface ChipsFilterProps {
  theme?: ThemeType;
  onDelete?: any;
  renderLabel?: any;
  chipColor?: ColorNames;
  hoverable?: boolean;
}

const renderDefaultLabel = (
  {
    item,
  }: {
    item: { key: string; value: string; count: number | string; label?: string };
  },
  labels: any = {},
) => {
  const label = item?.label || item.value;
  if (item?.key === 'release_date' || item?.key === 'publication_date') {
    const prefix = labels[`${item.key}_prefix`];
    return prefix ? `${prefix}: ${label}` : label;
  }
  return label;
};

const reduceChipStrategy = new Map<string, any>([
  ['publication_date', reducePublicationDateChip],
  ['release_date', reducePublicationDateChip],
]);

const ChipsFilter = ({
  theme = Theme.light,
  onDelete = () => null,
  renderLabel = renderDefaultLabel,
  chipColor,
  hoverable,
}: ChipsFilterProps) => {
  const isRevision = useIsRevision();
  const [dataLayerId, setDataLayerId] = React.useState('');
  const classes = useStyles({});
  const { queryParams, updateQueryParams, facets, resetParams, forceUpdate, pagination, status } =
    useContext(ElasticComponentContext);
  const { esLabels = {} } = usePageModels();
  const languagesLabels = useGetLanguageLabels();
  const labels = { ...esLabels, ...(!isEmpty(languagesLabels) && languagesLabels) };

  const tags = facets?.reduce<{ key: string; value: string; label?: string }[]>(
    (prev, { options, name }) => {
      const mappedOptions = options
        ?.filter((opt) => opt.selected)
        .map(({ value }) => ({ key: name, value }));
      return reduceChipStrategy.has(name)
        ? [...prev, ...reduceChipStrategy.get(name)({ list: mappedOptions })]
        : [...prev, ...reduceChipDefault({ list: mappedOptions, labels })];
    },
    [],
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const handleTag = (type: string, value: string | string[]) => {
    const allFilters = queryParams.filters[type] || [];
    const args = allFilters.filter((tag: string) => {
      return Array.isArray(value) ? !value.includes(tag) : tag !== value;
    });

    const variantName = (
      containerRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement
    )?.dataset?.datalayerVariant;
    datalayerPush({
      compRef: containerRef,
      args: {
        params: { filters: { ...queryParams.filters, [type]: args }, variantName },
      },
      event: 'gtm:custom:filters',
    });

    updateQueryParams(type, args, true);

    onDelete();
    setDataLayerId('apply_filters');
  };

  const handleKeyword = (keyword: string) => {
    updateQueryParams(
      'keywords',
      (queryParams.keywords as string[]).filter((item) => item !== keyword),
    );

    onDelete();
    setDataLayerId('apply_filters');
  };

  const filtersShowing = !!tags.length && resetParams && forceUpdate;
  const resetFilters = () => {
    if (resetParams && forceUpdate) {
      resetParams();
      forceUpdate();
    }
  };

  React.useEffect(() => {
    if (status !== LoadingStatusValues.loading) return;
    if (dataLayerId !== 'apply_filters') return;

    const filters = queryParams?.filters;
    const searchTerm = queryParams?.q?.trim();
    const searchFiltersObject = getDataLayerSearchFiltersObject(filters);
    const searchFilters = getDataLayerSearchFilters(filters);

    const dataLayer = {
      event: 'trackEvent-filterSearchResult',
      event_name: 'filter_search_results',
      ...(!!searchTerm && { search_term: searchTerm }),
      num_search_result: pagination?.totalResults || 0,
      index_num: String(pagination?.currentPage || 1),
      is_revision: isRevision,
      ...(!!searchFilters && { search_filters: searchFilters }),
      num_search_filters: getDataLayerNumFilters(filters),
      ...searchFiltersObject,
    };
    TagManager.dataLayer({ dataLayer });

    setDataLayerId('');
  }, [
    dataLayerId,
    isRevision,
    pagination?.currentPage,
    pagination?.totalResults,
    queryParams?.filters,
    queryParams?.q,
    status,
  ]);

  return (
    <div ref={containerRef}>
      {tags.map((item: any, index) => (
        <Chip
          // DO NOT DELETE 'fallback', it's being used in SolutionMarketplace.tsx
          label={renderLabel({ facet: item.key, item, fallback: renderDefaultLabel }, labels)}
          className={classes.chip}
          // eslint-disable-next-line react/no-array-index-key
          key={`${item.key}-${index}`}
          chipColor={chipColor}
          theme={theme}
          hoverable={hoverable}
          deleteIcon={<CloseIcon className="chip-color" fontSize="small" />}
          onDelete={() => handleTag(item.key, item.value)}
        />
      ))}

      {queryParams.keywords &&
        queryParams.keywords.map((keyword: any, id: number) => (
          <Chip
            label={keyword}
            className={classes.chip}
            // eslint-disable-next-line react/no-array-index-key
            key={id}
            chipColor={chipColor}
            theme={theme}
            hoverable={hoverable}
            deleteIcon={<CloseIcon className="chip-color" fontSize="small" />}
            onDelete={() => handleKeyword(keyword)}
          />
        ))}

      {filtersShowing && (
        <Button className={classes.chip} variant="text" onClick={resetFilters}>
          {labels?.reset_filters || 'Clear filters'}
        </Button>
      )}
    </div>
  );
};

export default memo(ChipsFilter);
